import { transformKeyCase } from 'Utils/transformKeyCase';
import { type SnakeCaseKeys, type AnyObject, type EmptyObject } from '@Types';
import {
    Frame,
    type FrameAttrs,
    type InitFrameAttrs,
    type CamelizedInitFrameAttrs,
    type AuthorComment,
    type FrameSnakeCasedAttrs,
} from 'Lessons';

export abstract class NonIguanaFrame<ExtraCamelizedAttrs extends AnyObject = EmptyObject> extends Frame {
    public attrs: FrameAttrs & ExtraCamelizedAttrs;
    abstract get frameType(): string; // has to be defined as a getter so we can access it in the constructor

    static new(attrs: InitFrameAttrs & SnakeCaseKeys<AnyObject>): Frame {
        // @ts-expect-error - Cannot create an instance of an abstract class
        return new this(attrs);
    }

    constructor(
        initAttrs: InitFrameAttrs & SnakeCaseKeys<ExtraCamelizedAttrs> = {} as InitFrameAttrs &
            SnakeCaseKeys<ExtraCamelizedAttrs>,
    ) {
        super(initAttrs);

        const camelCaseAttrs = transformKeyCase<CamelizedInitFrameAttrs & ExtraCamelizedAttrs>(initAttrs, {
            to: 'camelCase',

            // We're leaving the keys in this object snake_cased, since there is code that's reading
            // the annotations object on iguana- and non-iguana frames and it's easier if both behave
            // the same way.
            exclude: ['annotations'],
        });
        const frameType = this.validateFrameType(camelCaseAttrs.frameType);
        const attrs: FrameAttrs & ExtraCamelizedAttrs = {
            ...camelCaseAttrs,
            frameType,
            id: camelCaseAttrs.id || this.generateId(),
            annotations: camelCaseAttrs.annotations || ({} as FrameAttrs['annotations']),
            authorComments: camelCaseAttrs.authorComments || ([] as FrameAttrs['authorComments']),
        };
        this.attrs = attrs;
        this.validateFrameType(camelCaseAttrs.frameType);
    }

    get id(): string {
        return this.attrs.id;
    }

    get annotations(): FrameAttrs['annotations'] {
        return this.attrs.annotations;
    }

    set annotations(val: FrameAttrs['annotations']) {
        this.attrs.annotations = val;
    }

    get authorComments(): AuthorComment[] {
        return this.attrs.authorComments;
    }

    set authorComments(val: AuthorComment[]) {
        this.attrs.authorComments = val;
    }

    private validateFrameType(incomingFrameType?: string): string {
        if (incomingFrameType && incomingFrameType !== this.frameType) {
            throw new Error(`Frame type ${incomingFrameType} does not match expected type ${this.frameType}`);
        }
        return incomingFrameType || this.frameType;
    }

    asJson(): FrameSnakeCasedAttrs & SnakeCaseKeys<ExtraCamelizedAttrs> {
        return transformKeyCase<FrameSnakeCasedAttrs & SnakeCaseKeys<ExtraCamelizedAttrs>>(this.attrs, {
            to: 'snakeCase',
        });
    }
}
